import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import { createColumnHelper, Row } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Card from "@mui/material/Card";
import { CircularProgress, FormHelperText, Icon, Tooltip } from "@mui/material";
import Typography from "@/elements/Typography";
import Box from "@/elements/Box";
import Table from "@/elements/Table";
import Button from "@/elements/Button";
import Input from "@/elements/Input";
import colors from "@/theme/base/colors";
import {
  getDownloadFileQueryKey,
  getGetNodoIconaImageQueryKey,
  getGetNodoIconeQueryKey,
  NodoIconaDto,
  UploadedFileDto,
  useCreateNodoIcona,
  useDeleteNodoIcona,
  useUpdateNodoIcona,
  useUploadFile,
} from "@/api";
import {
  IS_NEW_KEY,
  useDbRelationsUpdateForm,
} from "@/utils/useDbRelationsUpdate";

const columnHelper = createColumnHelper<NodoIconaDto>();

export default function NodoIconeTableCard({
  nodoIcone,
  canEdit,
}: {
  nodoIcone: NodoIconaDto[];
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutateAsync: createNodoIcona } = useCreateNodoIcona();
  const { mutateAsync: updateNodoIcona } = useUpdateNodoIcona();
  const { mutateAsync: deleteNodoIcona } = useDeleteNodoIcona();

  const {
    data,
    control,
    handleSubmit,
    getChangeSubmitData,
    create,
    remove,
    restore,
    getFieldName,
    getEntityState,
  } = useDbRelationsUpdateForm(nodoIcone, {
    deleteChangeType: "deleted",
  });

  const [isSaving, setIsSaving] = useState(false);
  const editing = canEdit;

  const canEditRow = (row: Row<NodoIconaDto>) =>
    editing && !row.original.isDefault;

  const columns = useMemo(() => {
    const columns = [
      columnHelper.display({
        id: "icon",
        size: 40,
        header: t("nodoIconeTableCard.colonne.icona"),
        cell: ({ row }) => (
          <Controller
            name={getFieldName(row.index, "file")}
            control={control}
            rules={{
              required: t("nodoIconeTableCard.errori.file"),
            }}
            render={({ field, fieldState: { error } }) => {
              const file = field.value as UploadedFileDto | null | undefined;
              const originalFileId = row.original.file?.id;
              const previewUrl = file?.id
                ? row.original.id && file.id === originalFileId
                  ? getGetNodoIconaImageQueryKey(row.original.id)[0]
                  : getDownloadFileQueryKey(file.id)[0]
                : undefined;

              return (
                <NodoIconaFileField
                  value={file}
                  previewUrl={previewUrl}
                  disabled={!canEditRow(row)}
                  error={error?.message}
                  onChange={(value) => field.onChange(value)}
                />
              );
            }}
          />
        ),
      }),
      columnHelper.accessor("nome", {
        header: t("nodoIconeTableCard.colonne.nome"),
        cell: editing
          ? ({ row }) => (
              <Controller
                name={getFieldName(row.index, "nome")}
                control={control}
                rules={{ required: t("nodoIconeTableCard.errori.nome") }}
                render={({ field, fieldState: { error } }) => (
                  <Box>
                    <Input {...field} disabled={!canEditRow(row)} />
                    {error && (
                      <FormHelperText error>{error.message}</FormHelperText>
                    )}
                  </Box>
                )}
              />
            )
          : ({ getValue }) => getValue() || null,
      }),
      columnHelper.accessor("usedCount", {
        header: t("nodoIconeTableCard.colonne.uso"),
        size: 130,
        cell: ({ getValue }) => getValue() ?? 0,
      }),
      editing &&
        columnHelper.display({
          id: "buttons",
          header: "",
          size: 160,
          maxSize: 160,
          cell: ({ row }) => {
            const rowCanEdit = canEditRow(row);
            const isUsed = (row.original.usedCount ?? 0) > 0;
            const deleteDisabled = !rowCanEdit || isUsed;

            return (
              <>
                <Box display="flex" gap={1} flexDirection="row-reverse">
                  <Tooltip
                    title={
                      isUsed
                        ? t(
                            "nodoIconeTableCard.tooltip.nonEliminabilePercheUsata",
                            { count: row.original.usedCount }
                          )
                        : ""
                    }
                  >
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<Icon>delete_outlined</Icon>}
                        disabled={deleteDisabled}
                        onClick={() => {
                          remove(row.index);
                        }}
                      >
                        {t("azioni.elimina")}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                <Box
                  display="flex"
                  gap={1}
                  flexDirection="row-reverse"
                  className="deleted-row-visible-content flex"
                >
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    startIcon={<Icon>restore_from_trash</Icon>}
                    onClick={() => {
                      restore(row.index);
                    }}
                  >
                    {t("azioni.ripristina")}
                  </Button>
                </Box>
              </>
            );
          },
        }),
    ].filter((x) => x);
    return columns as Exclude<(typeof columns)[number], boolean>[];
  }, [canEditRow, control, editing, getFieldName, remove, restore, t]);

  const isRowDeleted = (row: Row<NodoIconaDto>) =>
    getEntityState(row.index) === "deleted";

  const onSubmit = async () => {
    const changes = getChangeSubmitData();
    if (changes.length === 0) {
      return;
    }

    setIsSaving(true);

    try {
      for (const change of changes) {
        if (change.changeType === "added") {
          const fileId = change.entity.file?.id;
          if (!fileId) {
            throw new Error("missing-file");
          }

          await createNodoIcona({
            data: {
              nome: change.entity.nome ?? "",
              fileId,
            },
          });
        }

        if (change.changeType === "updated") {
          if (!change.id) {
            continue;
          }

          await updateNodoIcona({
            id: change.id,
            data: {
              nome: change.entity.nome ?? "",
              fileId: change.entity.file?.id ?? null,
            },
          });
        }

        if (change.changeType === "deleted") {
          if (!change.id) {
            continue;
          }

          await deleteNodoIcona({ id: change.id });
        }
      }

      toast.success(t("nodoIconeTableCard.toast.salvate"));
      queryClient
        .invalidateQueries({
          queryKey: getGetNodoIconeQueryKey(),
        })
        .then();
    } catch (err) {
      toast.error(t("nodoIconeTableCard.toast.errore"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card sx={{ width: "100%" }}>
      <Box p={2} flex={1}>
        <Box display="flex" flexDirection="column" height="100%">
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text" fontWeight="medium">
              {t("nodoIconeTableCard.titolo")}
            </Typography>
            <Button
              size="small"
              color="primary"
              disabled={!editing}
              onClick={() => {
                create({
                  nome: "",
                  file: null,
                  isDefault: false,
                  usedCount: 0,
                });
              }}
            >
              {t("nodoIconeTableCard.azioni.aggiungi")}
            </Button>
          </Box>

          <Table
            columns={columns}
            data={data}
            sortable={false}
            initialSort={[{ id: IS_NEW_KEY, desc: false }]}
            isRowDeleted={isRowDeleted}
          />

          <Box display="flex" flexDirection="row-reverse">
            <Button
              size="small"
              color="primary"
              onClick={handleSubmit(onSubmit)}
              disabled={!editing || isSaving}
            >
              {t("azioni.salva")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Card>
  );
}

function NodoIconaFileField({
  value,
  previewUrl,
  disabled,
  error,
  onChange,
}: {
  value?: UploadedFileDto | null;
  previewUrl?: string;
  disabled: boolean;
  error?: string;
  onChange: (value: UploadedFileDto | null) => void;
}) {
  const { t } = useTranslation();
  const { mutateAsync } = useUploadFile();
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleUpload = async (file: File) => {
    setIsUploading(true);

    try {
      const result = await mutateAsync({
        data: { file },
      });
      onChange(result.data);
    } catch (err: any) {
      const status = err?.status;
      const msg =
        status === 413
          ? t("fileField.errori.fileTroppoGrande")
          : t("fileField.errori.caricamentoFallito");
      toast.error(msg);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      <Box display="flex" alignItems="center" gap={1}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 1,
            border: `1px solid ${colors.inputColors.borderColor.main}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={value?.fileName || "icon"}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          ) : (
            <Icon fontSize="small">image</Icon>
          )}
        </Box>

        <Box display="flex" flexDirection="column" gap={0.25}>
          <Button
            size="small"
            color="primary"
            disabled={disabled || isUploading}
            onClick={() => inputRef.current?.click()}
          >
            {value
              ? t("nodoIconeTableCard.azioni.sostituisci")
              : t("nodoIconeTableCard.azioni.carica")}
          </Button>
        </Box>

        {isUploading && <CircularProgress size={18} />}

        <input
          type="file"
          accept="image/*"
          ref={inputRef}
          style={{ display: "none" }}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file && !disabled) {
              handleUpload(file).then();
            }
            event.target.value = "";
          }}
        />
      </Box>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
}
